import { Badge, Grid, Menu } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBell,
  faBook,
  faBoxesStacked,
  faBoxOpen,
  faCalculator,
  faCalendarDays,
  faCoins,
  faChartColumn,
  faChartLine,
  faClipboardList,
  faComments,
  faDiagramProject,
  faFileInvoiceDollar,
  faFlask,
  faHospital,
  faIdBadge,
  faLayerGroup,
  faListUl,
  faMoneyBillWave,
  faPalette,
  faRightLeft,
  faRuler,
  faSliders,
  faStethoscope,
  faTableCellsLarge,
  faCircleInfo,
  faTruckFast,
  faUmbrellaBeach,
  faUserDoctor,
  faUserGroup,
  faCreditCard,
  faFileLines,
  faWallet,
  faStore,
  faReceipt,
  faHourglassHalf,
  faListCheck,
  faLock,
  faUserGear,
  faChartPie,
  faMoneyBillTransfer,
  faPercent,
  faLightbulb,
} from '@fortawesome/free-solid-svg-icons';
import { useMemo, useCallback, useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getStoredUser, isAuthenticated } from '../../utilities/authentication';
import { usePageAccess } from '../../hooks/usePageAccess';
import useTranslation from '../../localization/useTranslation';
import { getVisibleSidebarMenu, tourMenuTargetId } from '../../utilities/sidebarMenu';
import { SUPPORT_CHAT_PATH, openSupportChat } from '../../utilities/supportChat';
import { supportUnreadCountQueryOptions } from '../../query/fetchers/support';
import platformLogo from '../../images/logo/Dentrist-logo-dark.png';
import './SideBar.css';

const { useBreakpoint } = Grid;

const SUPPORT_CHAT_MENU_KEY = 'support_chat';

const normalizePath = (path) => (path ? path.replace(/\/$/, '').toLowerCase() : '');

const menuChildrenOf = (entry) =>
  Array.isArray(entry?.children)
    ? entry.children.filter((child) => child?.is_menu_item !== false)
    : [];

const iconMap = {
  dashboard: faChartLine,
  assessment: faChartColumn,
  calendar: faCalendarDays,
  settings: faSliders,
  badge: faIdBadge,
  apartment: faHospital,
  groups: faStethoscope,
  shopping_cart: faClipboardList,
  receipt: faFileInvoiceDollar,
  category: faLayerGroup,
  palette: faPalette,
  inventory: faBoxesStacked,
  inventory_2: faBoxOpen,
  payments: faMoneyBillWave,
  medical_services: faUserDoctor,
  people: faUserGroup,
  swap_horiz: faRightLeft,
  straighten: faRuler,
  view_quilt: faTableCellsLarge,
  local_shipping: faTruckFast,
  history: faListUl,
  flask: faFlask,
  info: faCircleInfo,
  beach_access: faUmbrellaBeach,
  credit_card: faCreditCard,
  account_statement: faFileLines,
  wallet: faWallet,
  account_tree: faDiagramProject,
  account_balance: faCoins,
  calculate: faCalculator,
  notifications: faBell,
  menu_book: faBook,
  store: faStore,
  receipt_long: faReceipt,
  hourglass: faHourglassHalf,
  fact_check: faListCheck,
  lock: faLock,
  engineering: faUserGear,
  insights: faChartPie,
  currency_exchange: faMoneyBillTransfer,
  percent: faPercent,
  lightbulb: faLightbulb,
};

const entryNameIconMap = {
  dashboard: faChartLine,
  reports: faChartColumn,
  orders: faClipboardList,
  invoices: faFileInvoiceDollar,
  clients: faStethoscope,
  dentists: faUserDoctor,
  users: faUserGroup,
  roles: faIdBadge,
  settings: faSliders,
  'setting.lab_info': faCircleInfo,
  'setting.subscription': faCreditCard,
  activities: faListUl,
  transactions: faRightLeft,
  expenses: faMoneyBillWave,
  finance: faCoins,
  calendar: faCalendarDays,
  client_wallet: faWallet,
  'platform_settings.notifications': faBell,
};

const NavIcon = ({ icon, compact = false }) => (
  <span className={`sidebar-nav-icon${compact ? ' sidebar-nav-icon--compact' : ''}`} aria-hidden="true">
    <FontAwesomeIcon icon={icon} />
  </span>
);

const SideBar = () => {
  const { menu, user: pageAccessUser } = usePageAccess();
  const user = pageAccessUser ?? getStoredUser();
  const location = useLocation();
  const { t } = useTranslation();
  const [openKeys, setOpenKeys] = useState([]);
  const screens = useBreakpoint();
  // Matches PrivateRoute: below lg the sidebar is a drawer and support chat is its own page;
  // on desktop the menu item opens the floating chat panel instead.
  const isMobile = screens.lg !== true;
  const authenticated = isAuthenticated();
  const supportUnreadQuery = useQuery(supportUnreadCountQueryOptions(authenticated));
  const supportUnreadCount = supportUnreadQuery.data ?? 0;

  const renderIcon = useCallback((iconName, entryName, compact = false) => {
    const resolvedIcon = iconName || (entryName === 'setting.lab_info' ? 'info' : null);
    const mapped =
      (resolvedIcon && iconMap[resolvedIcon]) ||
      (entryName && entryNameIconMap[entryName]) ||
      faListUl;

    return <NavIcon icon={mapped} compact={compact} />;
  }, []);

  const translateMenuLabel = useCallback(
    (entry) => {
      if (!entry) {
        return '';
      }

      const candidates = [];

      if (typeof entry?.meta?.translationKey === 'string') {
        candidates.push(entry.meta.translationKey);
      }

      if (typeof entry?.translation_key === 'string') {
        candidates.push(entry.translation_key);
      }

      if (typeof entry?.name === 'string' && entry.name.trim().length > 0) {
        const rawName = entry.name.trim();
        candidates.push(`sidebar.${rawName}`);
        candidates.push(`sidebar.${rawName.replace(/\./g, '_')}`);
      }

      if (typeof entry?.title === 'string' && entry.title.trim().length > 0) {
        const normalizedTitle = entry.title.trim().toLowerCase().replace(/\s+/g, '_');
        candidates.push(`sidebar.${normalizedTitle}`);
      }

      for (const key of candidates) {
        if (!key) {
          continue;
        }

        const translated = t(key);
        if (translated && translated !== key) {
          return translated;
        }
      }

      return entry?.title ?? entry?.name ?? '';
    },
    [t]
  );

  const visibleMenu = useMemo(() => getVisibleSidebarMenu(menu, user), [menu, user]);

  const mapMenuChild = useCallback((child) => ({
    key: child.name,
    icon: renderIcon(child.icon, child.name, true),
    label: child.path
      ? (
        <Link id={tourMenuTargetId(child.name)} to={child.path}>
          {translateMenuLabel(child)}
        </Link>
      )
      : (
        <span id={tourMenuTargetId(child.name)}>{translateMenuLabel(child)}</span>
      ),
  }), [translateMenuLabel, renderIcon]);

  const mapMenuEntry = useCallback((entry) => {
    const children = menuChildrenOf(entry);

    if (children.length > 0) {
      return {
        key: entry.name,
        icon: renderIcon(entry.icon, entry.name),
        label: translateMenuLabel(entry),
        children: children.map(mapMenuChild),
      };
    }

    const label = translateMenuLabel(entry);
    return {
      key: entry.name,
      icon: renderIcon(entry.icon, entry.name),
      label: entry.path
        ? <Link id={tourMenuTargetId(entry.name)} to={entry.path}>{label}</Link>
        : <span id={tourMenuTargetId(entry.name)}>{label}</span>,
    };
  }, [translateMenuLabel, renderIcon, mapMenuChild]);

  const items = useMemo(() => {
    const mapped = visibleMenu.map((entry) => mapMenuEntry(entry));

    const supportChatContent = (
      <>
        <span>{t('sidebar.support_chat')}</span>
        <Badge count={supportUnreadCount} size="small" />
      </>
    );

    const supportChatItem = {
      key: SUPPORT_CHAT_MENU_KEY,
      icon: <NavIcon icon={faComments} />,
      label: isMobile ? (
        <Link to={SUPPORT_CHAT_PATH} className="sidebar-support-chat-link">
          {supportChatContent}
        </Link>
      ) : (
        <span className="sidebar-support-chat-link">{supportChatContent}</span>
      ),
      onClick: isMobile ? undefined : openSupportChat,
    };

    const platformSettingsIndex = mapped.findIndex((entry) => entry.key === 'platform_settings');
    if (platformSettingsIndex < 0) {
      return [...mapped, supportChatItem];
    }

    const next = [...mapped];
    next.splice(platformSettingsIndex + 1, 0, supportChatItem);
    return next;
  }, [visibleMenu, mapMenuEntry, isMobile, supportUnreadCount, t]);

  const rootSubmenuKeys = useMemo(
    () => items.filter((entry) => Array.isArray(entry?.children) && entry.children.length > 0).map((entry) => entry.key),
    [items]
  );

  const { selectedKeys, submenuOpenKeys } = useMemo(() => {
    const current = normalizePath(location.pathname);

    if (current === SUPPORT_CHAT_PATH) {
      return { selectedKeys: [SUPPORT_CHAT_MENU_KEY], submenuOpenKeys: [] };
    }

    let bestMatch = null;

    visibleMenu.forEach((entry) => {
      const entryPath = normalizePath(entry.path);
      if (entryPath && current.startsWith(entryPath)) {
        if (!bestMatch || entryPath.length > bestMatch.pathLength) {
          bestMatch = { key: entry.name, open: [], pathLength: entryPath.length };
        }
      }

      (entry.children ?? []).forEach((child) => {
        const childPath = normalizePath(child.path);
        if (!childPath || !current.startsWith(childPath)) {
          return;
        }

        if (!bestMatch || childPath.length > bestMatch.pathLength) {
          bestMatch = { key: child.name, open: [entry.name], pathLength: childPath.length };
        }
      });
    });

    if (bestMatch) {
      return { selectedKeys: [bestMatch.key], submenuOpenKeys: bestMatch.open };
    }

    if (current.startsWith('/client-wallet')) {
      return { selectedKeys: ['client_wallet'], submenuOpenKeys: [] };
    }

    if (current.startsWith('/client-transactions') || current.startsWith('/dentist-transactions')) {
      return { selectedKeys: ['client_transactions'], submenuOpenKeys: [] };
    }

    if (current.startsWith('/client-settings') || current.startsWith('/dentist-settings')) {
      return { selectedKeys: ['client_settings'], submenuOpenKeys: [] };
    }

    if (current.startsWith('/setting/subscription')) {
      return { selectedKeys: ['setting.subscription'], submenuOpenKeys: ['platform_settings'] };
    }

    return { selectedKeys: [], submenuOpenKeys: [] };
  }, [visibleMenu, location.pathname]);

  const routeRootKey = submenuOpenKeys.find((key) => rootSubmenuKeys.includes(key)) ?? null;
  const routeOpenKeys = useMemo(
    () => (routeRootKey ? submenuOpenKeys : []),
    [routeRootKey, submenuOpenKeys]
  );

  useEffect(() => {
    if (routeOpenKeys.length === 0) {
      return;
    }

    setOpenKeys((prev) => {
      const current = Array.isArray(prev) ? prev : [];
      const nested = current.filter(
        (key) => !rootSubmenuKeys.includes(key) && !routeOpenKeys.includes(key)
      );
      const next = [...nested, ...routeOpenKeys];
      const areSame =
        current.length === next.length && current.every((key, idx) => key === next[idx]);
      return areSame ? current : next;
    });
  }, [location.pathname, routeOpenKeys, rootSubmenuKeys]);

  const onOpenChange = useCallback(
    (keys) => {
      setOpenKeys((prev) => {
        const current = Array.isArray(prev) ? prev : [];
        const latestRootKey = keys.find(
          (key) => !current.includes(key) && rootSubmenuKeys.includes(key)
        );

        const next = latestRootKey
          ? keys.filter((key) => key === latestRootKey || !rootSubmenuKeys.includes(key))
          : keys;

        const areSame =
          current.length === next.length && current.every((key, idx) => key === next[idx]);

        return areSame ? current : next;
      });
    },
    [rootSubmenuKeys]
  );

  return (
    <div id="tour-sidebar" className="sidebar-brand-shell">
      <div className="sidebar-brand">
        <Link to="/" className="sidebar-brand__link">
          <img
            src={platformLogo}
            alt="Dentrist"
            className="sidebar-brand__logo"
          />
        </Link>
      </div>
      {authenticated ? (
        <Menu
          mode="inline"
          theme="dark"
          className="sidebar-menu"
          selectedKeys={selectedKeys}
          openKeys={openKeys}
          onOpenChange={onOpenChange}
          items={items}
        />
      ) : (
        ''
      )}
    </div>
  );
};

export default SideBar;
